/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Host, Inject, Input, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  CELL_TEXT_PRIMARY_CLASS,
  code3Formatter,
  falsyCellRenderer, GREEN_MARKER_COLOR, rendererColorCircle,
  rendererIconByName, truthyCellRenderer,
} from '@gh/common-internal-shared';
import { getDialogInitData, getDialogInput, MutableSelectionModel, NumberTypes } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import { buildNumberColumn, GridDefinition, GridDefinitionFactory } from '@gh/core-ui-grid';
import { flatMap } from 'lodash';
import { getPbComponentItemFinderListItemId, isPbStructureSuitable } from '../../';
import { PbComponentItemFinderDialogConfig } from '../../resources/pb-component-item-finder-business.service';
import { PbComponentItemFinderListItem } from '../../resources/pb-component-item-finder.model';
import { AbstractPbComponentItemFinderViewComponent } from './abstract-pb-component-item-finder-view-component';
import { PbComponentItemFinderDialogComponent } from './pb-component-item-finder-dialog.component';

@Component({
  selector: 'gh-pb-component-item-finder-full-view',
  template: require('./pb-component-item-finder-full-view.component.html'),
  styles: [require('./pb-component-item-finder-full-view.component.scss')],
})
export class PbComponentItemFinderFullViewComponent extends AbstractPbComponentItemFinderViewComponent
  implements OnInit {

  input: PbComponentItemFinderDialogConfig;
  selectionModel: MutableSelectionModel<PbComponentItemFinderListItem>;
  structureGridDefinition: GridDefinition;

  currentStructures: PbComponentItemFinderListItem[];

  private structures: PbComponentItemFinderListItem[];

  constructor(private gridDefinitionFactory: GridDefinitionFactory,
              private translationService: TranslationService,
              @Host() private finderDialog: PbComponentItemFinderDialogComponent,
              @Inject(MAT_DIALOG_DATA) private dialogData: any) {
    super();
  }

  @Input()
  set name(name: string) {
    const nameRe = name ? new RegExp(`${name.trim()}`, 'i') : void 0;
    this.currentStructures = nameRe ?
      this.structures.filter((structure) => isPbStructureSuitable(structure, nameRe)) : this.structures;
  }

  ngOnInit(): void {
    const { hierarchy } = getDialogInitData(this.dialogData);

    this.input = getDialogInput(this.dialogData);
    this.selectionModel = this.finderDialog.selectionModel;

    const structures = flatMap(hierarchy, ({ items }) => items || []);
    this.structures = structures;
    this.currentStructures = structures;

    this.structureGridDefinition = this.gridDefinitionFactory.buildManual(
      [
        {
          field: 'code',
          width: 150,
          headerName: this.translationService.get('lbl_code'),
          cellClass: CELL_TEXT_PRIMARY_CLASS,
          sort: 'asc',
          suppressSizeToFit: true,
        },
        { field: 'name', width: 60, headerName: this.translationService.get('lbl_name') },
        {
          field: 'costCenterCode',
          width: 75,
          headerName: this.translationService.get('lbl_cc_number'),
          valueFormatter: code3Formatter,
          suppressSizeToFit: true,
        },
        {
          field: 'isActive',
          width: 90,
          headerName: this.translationService.get('lbl_deleted'),
          cellRenderer: falsyCellRenderer(rendererIconByName('done')),
          suppressSizeToFit: true,
        },
        {
          field: 'uomCode',
          width: 70,
          headerName: this.translationService.get('lbl_uom_abbreviation'),
          suppressSizeToFit: true,
        },
        {
          field: 'price',
          width: 90,
          headerName: this.translationService.get('lbl_price'),
          suppressSizeToFit: true,
          ...buildNumberColumn(NumberTypes.Price),
        },
        {
          field: 'origQuantity',
          width: 110,
          headerName: this.translationService.get('lbl_orig_qty'),
          suppressSizeToFit: true,
          ...buildNumberColumn(NumberTypes.Quantity),
        },
        {
          field: 'origQuantityComponent',
          width: 110,
          headerName: this.translationService.get('lbl_cmp_qty'),
          suppressSizeToFit: true,
          ...buildNumberColumn(NumberTypes.Quantity),
        },
      ],
      {
        getRowNodeId: getPbComponentItemFinderListItemId,
      });
  }
}
